import React, { useState, useCallback, useEffect } from "react";
import { Box, Typography, Button, TextField, MenuItem, Grid, Paper, CircularProgress } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import registerApi from "./registerApi";
import { asSegments } from "./segments";
import { IMPACT_LEVELS, asList } from "./impactConstants";

const sectionLabelSx = {
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    color: "text.secondary",
    textTransform: "uppercase",
    mb: 2,
};

// Create / edit one Impact Register row.
//
// The workspace hosts it in a drawer so the segment panel and the table stay on
// screen. `impactId` present means edit. `segments` come from the host, which has
// already fetched them, so opening the drawer costs no extra round-trip.
//
// The form asks for four things about the change — what it is, how things work
// today, how they will work, and anything else worth knowing — plus where it sits
// (its segments) and how big it is. Fields the BE still holds but the form no
// longer asks about (pillars, change actions, success measure, the impact
// description, starting attitude) are never SENT, so editing an older impact
// leaves whatever it already had there untouched.
export default function ImpactForm({
    projectId,
    impactId,
    impact: impactProp,
    segments: segmentsProp,
    // False when the host could not load the segments. The form then neither
    // shows nor SENDS segment values: an empty map on a PATCH would wipe every
    // value the impact already has.
    segmentsAvailable = true,
    onSaved,
    onCancel,
}) {
    const isEdit = Boolean(impactId);
    const intl = useIntl();
    const { enqueueSnackbar } = useSnackbar();

    // ── Form fields, in the order they're rendered ──
    const [whatIsChanging, setWhatIsChanging] = useState("");
    const [asIs, setAsIs] = useState("");
    const [toBe, setToBe] = useState("");
    // "Additional info" — stored in the BE's `goal`, so the text older impacts
    // already carry there shows up here.
    const [goal, setGoal] = useState("");
    // One choice per segment, as { segment id: choice id (string) }. "" or a
    // missing key is "Any" — the impact applies to every choice in that
    // segment. Every segment starts there; narrowing it is a deliberate choice.
    const [segmentValues, setSegmentValues] = useState({});
    const [level, setLevel] = useState("medium");

    const [whatError, setWhatError] = useState(false);

    const [segments, setSegments] = useState(() => (segmentsProp ? asSegments(segmentsProp) : []));
    const [segmentsLoading, setSegmentsLoading] = useState(!segmentsProp);
    const [prefillLoading, setPrefillLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);

    const close = useCallback(() => onCancel(), [onCancel]);

    const applyImpact = useCallback((impact) => {
        setWhatIsChanging(impact.what_is_changing || "");
        setAsIs(impact.as_is || "");
        setToBe(impact.to_be || "");
        setGoal(impact.goal || "");
        setSegmentValues(
            Object.fromEntries(
                Object.entries(impact.segment_values || {}).map(([id, value]) => [String(id), String(value)])
            )
        );
        setLevel((impact.level || "medium").toLowerCase());
    }, []);

    // The segment pickers are THIS project's segments. Fetched only when the host
    // did not hand them down.
    useEffect(() => {
        if (segmentsProp || !projectId) return;
        let cancelled = false;
        (async () => {
            try {
                setSegmentsLoading(true);
                const data = await registerApi.getSegments(projectId);
                if (!cancelled) setSegments(asSegments(data));
            } catch (error) {
                console.error("Failed to load impact form data:", error);
                if (!cancelled) {
                    enqueueSnackbar(
                        intl.formatMessage({
                            id: "impact.failed_to_load_form_data",
                            defaultMessage: "Failed to load segments",
                        }),
                        { variant: "error" }
                    );
                }
            } finally {
                if (!cancelled) setSegmentsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [segmentsProp, projectId, enqueueSnackbar, intl]);

    // Prefill in edit mode: prefer the impact handed straight to us (drawer prop
    // or navigation state), else fetch this project's impacts and find it by id.
    useEffect(() => {
        if (!isEdit) return;
        if (!projectId) return;
        const fromState = impactProp;
        if (fromState && String(fromState.id) === String(impactId)) {
            applyImpact(fromState);
            setPrefillLoading(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                setPrefillLoading(true);
                const data = await registerApi.getImpacts(projectId);
                const match = asList(data).find((row) => String(row.id) === String(impactId));
                if (cancelled) return;
                if (match) {
                    applyImpact(match);
                } else {
                    enqueueSnackbar(
                        intl.formatMessage({ id: "impact.not_found", defaultMessage: "Impact not found" }),
                        { variant: "error" }
                    );
                }
            } catch (error) {
                console.error("Failed to load impact:", error);
            } finally {
                if (!cancelled) setPrefillLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isEdit, projectId, impactId, impactProp, applyImpact, enqueueSnackbar, intl]);

    // ── Save ──
    const handleSave = async () => {
        const missingWhat = !whatIsChanging.trim();
        setWhatError(missingWhat);
        if (missingWhat) return;

        // `source` and `verification_status` are deliberately absent — the BE
        // sets both, and this screen has no verify controls.
        const payload = {
            what_is_changing: whatIsChanging.trim(),
            as_is: asIs.trim(),
            to_be: toBe.trim(),
            goal: goal.trim(),
            level,
            // The whole map, every time: a PATCH that sent only the set ones
            // could never clear a value. Segments left at Any are left out.
            ...(segmentsAvailable
                ? {
                      segment_values: Object.fromEntries(
                          Object.entries(segmentValues)
                              .filter(([, value]) => value !== "")
                              .map(([id, value]) => [id, Number(value)])
                      ),
                  }
                : {}),
        };
        try {
            setSaving(true);
            const saved = isEdit
                ? await registerApi.updateImpact(projectId, impactId, payload)
                : await registerApi.createImpact(projectId, payload);
            enqueueSnackbar(intl.formatMessage({ id: "impact.saved", defaultMessage: "Impact saved" }), {
                variant: "success",
            });
            onSaved(saved);
        } catch (error) {
            console.error("Failed to save impact:", error);
            // DRF replies with { field: [message] }. Surfacing the first one beats a
            // blank "Failed to save" when the BE rejects a value.
            const data = error?.response?.data;
            const detail =
                data && typeof data === "object" && !Array.isArray(data)
                    ? Object.values(data).flat().filter((m) => typeof m === "string")[0]
                    : null;
            enqueueSnackbar(
                detail ||
                    intl.formatMessage({ id: "impact.save_failed", defaultMessage: "Failed to save impact" }),
                { variant: "error" }
            );
        } finally {
            setSaving(false);
        }
    };

    // ── Guard states ────────────────────────────────────────────────────────
    if (prefillLoading || segmentsLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress sx={{ color: "#00438E" }} />
            </Box>
        );
    }

    return (
        <Box>
            {/* THE CHANGE — what it is, today -> tomorrow, and anything else */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography sx={sectionLabelSx}>
                    <FormattedMessage id="impact.section_change" defaultMessage="The change" />
                </Typography>
                <TextField
                    fullWidth
                    required
                    label={intl.formatMessage({
                        id: "impact.field_what_is_changing",
                        defaultMessage: "What is changing",
                    })}
                    value={whatIsChanging}
                    error={whatError}
                    helperText={
                        whatError
                            ? intl.formatMessage({
                                  id: "impact.what_is_changing_required",
                                  defaultMessage: "What is changing is required",
                              })
                            : intl.formatMessage({
                                  id: "impact.field_what_is_changing_help",
                                  defaultMessage: "A short title for the change, e.g. “Timesheets move to the new HR system”",
                              })
                    }
                    onChange={(e) => {
                        setWhatIsChanging(e.target.value);
                        if (whatError) setWhatError(false);
                    }}
                    sx={{ mb: 2.5 }}
                />
                {/* Today -> tomorrow, side by side so the two read as one
                    before/after pair. On a narrow drawer they stack and the
                    arrow turns to point down. */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr auto 1fr" },
                        alignItems: "start",
                        columnGap: 1,
                        mb: 2.5,
                    }}
                >
                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        label={intl.formatMessage({ id: "impact.field_as_is", defaultMessage: "As-is" })}
                        helperText={intl.formatMessage({
                            id: "impact.field_as_is_help",
                            defaultMessage: "How it works today",
                        })}
                        value={asIs}
                        onChange={(e) => setAsIs(e.target.value)}
                    />
                    <Box
                        aria-hidden="true"
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            // Level with the middle of the boxes, not their helper text.
                            height: { sm: 125 },
                            py: { xs: 0.5, sm: 0 },
                            color: "#00438E",
                        }}
                    >
                        <ArrowForwardIcon sx={{ transform: { xs: "rotate(90deg)", sm: "none" } }} />
                    </Box>
                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        label={intl.formatMessage({ id: "impact.field_to_be", defaultMessage: "Future state" })}
                        helperText={intl.formatMessage({
                            id: "impact.field_to_be_help",
                            defaultMessage: "How it will work after the change",
                        })}
                        value={toBe}
                        onChange={(e) => setToBe(e.target.value)}
                    />
                </Box>
                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label={intl.formatMessage({
                        id: "impact.field_additional_info",
                        defaultMessage: "Additional info",
                    })}
                    helperText={intl.formatMessage({
                        id: "impact.field_additional_info_help",
                        defaultMessage: "Anything else worth knowing — context, dependencies, open questions",
                    })}
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                />
            </Paper>

            {/* WHERE IT SITS AND HOW BIG */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography sx={sectionLabelSx}>
                    <FormattedMessage id="impact.section_who" defaultMessage="Who it lands on" />
                </Typography>
                <Grid container spacing={2}>
                    {segments.map((segment) => (
                        <Grid item xs={12} sm={6} key={segment.id}>
                            <TextField
                                select
                                fullWidth
                                label={segment.title}
                                value={segmentValues[String(segment.id)] || ""}
                                helperText=" "
                                SelectProps={{ displayEmpty: true }}
                                InputLabelProps={{ shrink: true }}
                                onChange={(e) =>
                                    setSegmentValues((prev) => ({ ...prev, [String(segment.id)]: e.target.value }))
                                }
                            >
                                {/* Any = no value: the impact applies to every choice. */}
                                <MenuItem value="">
                                    {intl.formatMessage({ id: "impact.segment_any", defaultMessage: "Any" })}
                                </MenuItem>
                                {segment.choices.map((choice) => (
                                    <MenuItem key={choice.id} value={String(choice.id)}>
                                        {choice.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    ))}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            label={intl.formatMessage({ id: "impact.field_level", defaultMessage: "Level" })}
                            value={level}
                            helperText=" "
                            onChange={(e) => setLevel(e.target.value)}
                        >
                            {IMPACT_LEVELS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {intl.formatMessage({
                                        id: `impact.level_${opt.value}`,
                                        defaultMessage: opt.label,
                                    })}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
            </Paper>

            {/* ACTIONS */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
                <Button onClick={close} disabled={saving} sx={{ color: "text.secondary" }}>
                    <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
                    sx={{ backgroundColor: "#00438E", "&:hover": { backgroundColor: "#003A75" } }}
                >
                    <FormattedMessage id="general.save" defaultMessage="Save" />
                </Button>
            </Box>
        </Box>
    );
}
